import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
  RefreshControl,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import SparcWalkBegin from "./sparcwalk/SparcWalkBegin";
import SparcWalkInspectionScreen from "./sparcwalk/SparcWalkInspectionScreen";
import SparcWalkMetrics from "./sparcwalk/SparcWalkMetrics";
import SparcWalkWelcome from "./sparcwalk/SparcWalkWelcome";
import SparcWalkModal from "./sparcwalk/SparcWalkModal";
import SparcWalkErrorModal from "./sparcwalk/components/SparcWalkErrorModal";
import MobileFilterOverlay from "../components/MobileFilterOverlay";
import { SparcWalkRowData } from "../types/sparcWalk";
import { DropdownItem } from "../types/observation";
import { setSelectedWalk, setZone, clearSuccessMessage } from "../store/slices/sparcWalkSlice";
import { createApiClient, apiClient, getLocalTimestamp } from "../api/apiClient";
import { sparcWalkTableColumns } from "../data";
import {
  SparcWalkProps,
  SparcWalkRowDataWithNavigation,
  FilterState,
} from "./sparcwalk/SparcWalkTypes";
import { selectCategoryOptions } from "./sparcwalk/utils/sparcWalkConstants";
import { isIOSMobile, isMobileDevice } from "./sparcwalk/utils/deviceDetection";
import { buildFilterOptions } from "./sparcwalk/utils/filterOptionsBuilder";
import useSparcWalkData from "./sparcwalk/hooks/useSparcWalkData";
import useZoneOptions from "./sparcwalk/hooks/useZoneOptions";
import useSparcWalkFilters from "./sparcwalk/hooks/useSparcWalkFilters";
import useSparcWalkFilterHandlers from "./sparcwalk/hooks/useSparcWalkFilterHandlers";
import { useSelectedLocation } from "../hooks";
import {
  handleSparcWalkErrorWithFriendlyMessage,
  SPARC_WALK_API_NAMES,
} from "../store/utils/sparcWalkErrorHandler";
import { useTheme } from "../theme";

const SparcWalkScreen: React.FC<SparcWalkProps> = ({ onBackPress }) => {
  const dispatch = useDispatch();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const { walkType, hiddenWalk, successMessage } = useSelector(
    (state: RootState) => state.sparcWalk
  );
  const selectedLocation = useSelectedLocation();

  const [selectedRowData, setSelectedRowData] = useState<SparcWalkRowData | null>(null);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isFilterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);
  const [showInspectionScreen, setShowInspectionScreen] = useState(false);
  const [beginZone, setBeginZone] = useState<DropdownItem | null>(null);
  const [beginCategory, setBeginCategory] = useState<DropdownItem | null>(null);
  const [detailedData, setDetailedData] = useState<any>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [zoneOptions, setZoneOptions] = useState<DropdownItem[]>([]);
  const [sparcWalkData, setSparcWalkData] = useState<SparcWalkRowData[]>([]);
  const [fullSparcWalkData, _setFullSparcWalkData] = useState<SparcWalkRowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [page] = useState(1);
  const initialFilterState: FilterState = {
    zone: null,
    category: null,
    Created_By: "",
    Modified_By: "",
    action: "",
    period: "",
    week: "",
    year: "",
  };
  const [filters, setFilters] = useState(initialFilterState);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterState);
  const [refreshing, setRefreshing] = useState(false);

  const isDisabled = detailedData?.status?.toLowerCase() === "completed";
  
    // Table data mapping
  const tableData = sparcWalkData.map((row) => ({
    ...row,
    navigationData: {
      id: row.id,
      zone: row.zone,
      category: row.category || row.details?.split(" - ")[0] || "",
    },
    action: row.action || row.status || "new",
    status: row.status || row.action || "new",
    id: row.id,
  }));

  // Filter options
  const filterOptions = buildFilterOptions({
    filters,
    zoneOptions,
    fullSparcWalkData,
  });
  
  // Auto-apply filters for web/iPad (debounced)
  const applyFiltersDebounceRef = useRef<any>(null);

  // Custom hooks for data fetching
  const { fetchZoneOptions } = useZoneOptions({ setZoneOptions });
  const { fetchSparcWalkData } = useSparcWalkData({
    setSparcWalkData,
    setFullSparcWalkData: _setFullSparcWalkData,
    setIsLoading,
    setApiError,
  });

  // Custom hooks for filtering
  const { applyFiltersForIOSMobile, applyFilters } = useSparcWalkFilters({
    filters,
    fullSparcWalkData,
    setSparcWalkData,
    setAppliedFilters,
    setApiError,
    setIsLoading,
    selectedLocation,
    walkType,
    hiddenWalk,
    page,
  });

  // Custom hook for filter handlers
  const { handleWebFilterChange, handleMobileFilterChange, resetFilters } =
    useSparcWalkFilterHandlers({
      filters,
      setFilters,
      appliedFilters,
      setAppliedFilters,
      applyFilters,
      applyFiltersForIOSMobile,
      setSparcWalkData,
      fullSparcWalkData,
    });

  // Row click handler
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      // Use applyFilters to re-fetch with currently active filters preserved
      await Promise.race([Promise.all([fetchZoneOptions(), applyFilters()]), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  }, [fetchZoneOptions, applyFilters]);

  const handleRowClick = async (rowData: SparcWalkRowDataWithNavigation) => {
    try {
      setIsDetailLoading(true);
      setDetailError(null);
      setSelectedRowData(rowData);

      if (rowData.columnKey !== "details") {
        setOverlayVisible(true);
      }

      const api = createApiClient();
      const response = await apiClient(
        api,
        `/sparc-walks/${rowData.id}`,
        "GET"
      );
      
      if (!response || !response.walkData) {
        throw new Error("Invalid walk details received");
      }
      
      const detailedWalkData = response.walkData;

      if (rowData.columnKey === "details") {
        const formattedWalkData = {
          ...detailedWalkData,
          id: rowData.id,
          walkData: {
            conversedWith: detailedWalkData?.conversedWith || "",
            observations: detailedWalkData?.notes || "",
            sparcSolutions: detailedWalkData?.sparcSolutions || "",
            sparcExpectedAnswers: detailedWalkData?.sparcExpectedAnswers || "",
            notes: detailedWalkData?.notes || "",
            status: (detailedWalkData?.status || "new").toLowerCase(),
            managerSignature: detailedWalkData?.managerSignature || null,
            miscellImage: detailedWalkData?.miscellImage || null,
            miscellImage2: detailedWalkData?.miscellImage2 || null,
            miscellImageDesc: detailedWalkData?.miscellImageDesc || null,
            miscellImage2Desc: detailedWalkData?.miscellImage2Desc || null,
            updatedImg1: !!detailedWalkData?.updatedImg1,
            updatedImg2: !!detailedWalkData?.updatedImg2,
            updatedManagerSign: !!detailedWalkData?.updatedManagerSign,
            created: detailedWalkData?.created || getLocalTimestamp(),
            createdBy: detailedWalkData?.createdBy || "",
            modified: detailedWalkData?.modified || getLocalTimestamp(),
            modifiedBy: detailedWalkData?.modifiedBy || "",
            toolsMapping: (response?.toolsMapping || []).map((tool: any) => ({
              toolId: parseInt(tool.toolId || "0", 10),
            })),
            questionsMapping: (response?.questionsMapping || []).map(
              (q: any) => ({
                toolId: parseInt(q.TOOL_ID || q.toolId || "0", 10),
              })
            ),
            sparcWalkImages: (response?.sparcWalkImages || []).map(
              (img: any) => ({
                id: img.ImageId,
                observationId: img.ObservationId || null,
                imageUrl: img.imageUrl || img.ImageBase64,
                description: img.Description || "",
              })
            ),
          },
        };

        dispatch(setSelectedWalk(formattedWalkData));

        setBeginZone({
          id: String(rowData.id),
          displayText: rowData.zone || "",
        });

        setBeginCategory({
          id: String(rowData.id),
          displayText: rowData.details?.split(" - ")[0] || "",
        });

        setShowInspectionScreen(true);
      } else {
        setDetailedData({
          walkData: {
            conversedWith: detailedWalkData?.conversedWith || "",
            observations: detailedWalkData?.notes || "",
            sparcSolutions: detailedWalkData?.sparcSolutions || "",
            sparcExpectedAnswers: detailedWalkData?.sparcExpectedAnswers || "",
            notes: detailedWalkData?.notes || "",
            status: (detailedWalkData?.status || "new").toLowerCase(),
            managerSignature: detailedWalkData?.managerSignature || "",
            miscellImage: detailedWalkData?.miscellImage || "",
            miscellImage2: detailedWalkData?.miscellImage2 || "",
            miscellImageDesc: detailedWalkData?.miscellImageDesc || "",
            miscellImage2Desc: detailedWalkData?.miscellImage2Desc || "",
          },
        });
      }
    } catch (error) {
      console.error("Error fetching walk details:", error);
      
      // Use centralized error handler with modal overlay
      handleSparcWalkErrorWithFriendlyMessage(
        SPARC_WALK_API_NAMES.FETCH_WALK_DETAILS,
        error,
        true, // Show modal overlay
        {
          walkId: rowData.id,
          walkType: rowData.walkType,
          zone: rowData.zone,
          category: rowData.details?.split(" - ")[0],
        }
      );
      
      setDetailError(
        error instanceof Error ? error.message : "Failed to fetch walk details"
      );
      if (!isOverlayVisible) {
        setOverlayVisible(true);
      }
    } finally {
      setIsDetailLoading(false);
    }
  };

  const handleMobileFilterPress = () => {
    setMobileFilterOverlayVisible(true);
  };

  const handleMobileFilterCancel = () => {
    setMobileFilterOverlayVisible(false);
  };

  // Mobile filter proceed handler
  const handleMobileFilterProceed = async (
    nextFilters: Partial<Record<string, any>> = {}
  ) => {
    const isIOSMobileDevice = isIOSMobile();

    // Normalize zone and category values
    const zoneValue = Object.prototype.hasOwnProperty.call(nextFilters, "zone")
      ? (nextFilters.zone as DropdownItem | null)
      : filters.zone;
    const categoryValue = Object.prototype.hasOwnProperty.call(
      nextFilters,
      "category"
    )
      ? (nextFilters.category as DropdownItem | null)
      : filters.category;

    const normalizedZone =
      zoneValue && zoneValue.displayText === "All" ? null : zoneValue;
    const normalizedCategory =
      categoryValue && categoryValue.displayText === "All"
        ? null
        : categoryValue;

    const normalizedFilters: FilterState = {
      zone: normalizedZone ?? null,
      category: normalizedCategory ?? null,
      Created_By: Object.prototype.hasOwnProperty.call(
        nextFilters,
        "Created_By"
      )
        ? (nextFilters.Created_By as string) || ""
        : filters.Created_By,
      Modified_By: Object.prototype.hasOwnProperty.call(
        nextFilters,
        "Modified_By"
      )
        ? (nextFilters.Modified_By as string) || ""
        : filters.Modified_By,
      action: Object.prototype.hasOwnProperty.call(nextFilters, "action")
        ? (nextFilters.action as string) || ""
        : filters.action,
      period: Object.prototype.hasOwnProperty.call(nextFilters, "period")
        ? (nextFilters.period as string) || ""
        : filters.period,
      week: Object.prototype.hasOwnProperty.call(nextFilters, "week")
        ? (nextFilters.week as string) || ""
        : filters.week,
      year: Object.prototype.hasOwnProperty.call(nextFilters, "year")
        ? (nextFilters.year as string) || ""
        : filters.year,
    };

    // Update filters state
    setFilters(normalizedFilters);

    if (isIOSMobileDevice) {
      // Apply local filtering first for instant feedback
      applyFiltersForIOSMobile(normalizedFilters);
      // Then sync with server to ensure data consistency
      await applyFilters(normalizedFilters);
    } else {
      // Web/iPad - just use server filtering
      await applyFilters(normalizedFilters);
    }

    setMobileFilterOverlayVisible(false);
  };

  // Unified filter handler
  const handleFilterChange = (key: string, value: any) => {
    const isIOSMobileDevice = isIOSMobile();

    if (isIOSMobileDevice) {
      handleMobileFilterChange(key as keyof FilterState, value);
    } else {
      handleWebFilterChange(key as keyof FilterState, value);
    }
  };

  useEffect(() => {
    fetchZoneOptions();
    fetchSparcWalkData();
  }, [fetchZoneOptions, fetchSparcWalkData, selectedLocation]);

  useEffect(() => {
    if (applyFiltersDebounceRef.current) {
      clearTimeout(applyFiltersDebounceRef.current);
      applyFiltersDebounceRef.current = null;
    }

    const isIOSMobileDevice = isIOSMobile();

    if (isIOSMobileDevice) {
      return;
    }

    applyFiltersDebounceRef.current = setTimeout(() => {
      try {
        const filtersChanged =
          JSON.stringify(filters) !== JSON.stringify(appliedFilters);
        if (filtersChanged) {
          applyFilters(filters);
        }
      } catch (err) {
        console.error("Error auto-applying filters:", err);
      }
    }, 250);

    return () => {
      if (applyFiltersDebounceRef.current) {
        clearTimeout(applyFiltersDebounceRef.current);
        applyFiltersDebounceRef.current = null;
      }
    };
  }, [filters, appliedFilters, applyFilters]);
  
  // Clear success message after 5 seconds (matching Leadership Walk behavior)
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);
  
  if(isMobileFilterOverlayVisible) {
    return(
      <MobileFilterOverlay
          visible={isMobileFilterOverlayVisible}
          onDismiss={handleMobileFilterCancel}
          filters={filterOptions}
          onFilterChange={handleFilterChange}
          onProceed={handleMobileFilterProceed}
          onCancel={handleMobileFilterCancel}
          currentFilters={filters}
        />
    );
  }

  if (showInspectionScreen && beginZone && beginCategory) {
    return (
      <View
        style={[
          styles.inspectionContainer,
          isDark && { backgroundColor: colors.background.page },
        ]}
      >
        <SparcWalkInspectionScreen
          selectedZone={beginZone}
          selectedCategory={beginCategory}
          onBackPress={() => {
            setShowInspectionScreen(false);
            setBeginZone(null);
            setBeginCategory(null);
            fetchSparcWalkData();
          }}
          onNavigateHome={onBackPress}
          onSubmitSuccess={(id, status) => {
            console.log(
              `SPARC Walk submitted with ID: ${id}, status: ${status}`
            );
          }}
          selectedWalkData={detailedData}
          isDisabled={isDisabled}
        />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={[
        styles.keyboardView,
        isDark && { backgroundColor: colors.background.page },
      ]}
    >
      <ScrollView
        style={[
          styles.scrollView,
          isDark && { backgroundColor: colors.background.page },
        ]}
        contentContainerStyle={[
          styles.container,
          isDark && { backgroundColor: colors.background.page },
        ]}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={isDark ? colors.brand.primary : "#0066CC"}
            colors={[isDark ? colors.brand.primary : "#0066CC"]}
            title="Refreshing..."
            titleColor={isDark ? colors.text.secondary : undefined}
          />
        }
      >
        {successMessage && (
          <View
            style={[
              styles.successMessageContainer,
              isDark && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
          >
            <MaterialIcons
              name="check-circle"
              size={24}
              color={isDark ? colors.status.success : "#008242"}
              style={styles.successIcon}
            />
            <Text
              style={[
                styles.successMessageText,
                isDark && { color: colors.text.primary },
              ]}
            >
              {successMessage}
            </Text>
          </View>
        )}

        {detailError && (
          <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>
            {detailError}
          </Text>
        )}

        <SparcWalkWelcome
          openFilterOverlay={() => setFilterOverlayVisible(true)}
          clearWalk={() => {
            dispatch(setSelectedWalk(null));
            dispatch(setZone("begin"));
          }}
        />

        <SparcWalkMetrics
          filterOptions={filterOptions}
          handleFilterChange={handleFilterChange}
          isLoading={isLoading}
          apiError={apiError}
          sparcWalkData={sparcWalkData}
          tableColumns={sparcWalkTableColumns}
          handleRowClick={handleRowClick}
          tableData={tableData}
          isMobile={isMobileDevice()}
          resetFilters={resetFilters}
          showMobileFilterButton={isIOSMobile()}
          onMobileFilterPress={handleMobileFilterPress}
        />

        <SparcWalkModal
          isOverlayVisible={isOverlayVisible}
          setOverlayVisible={setOverlayVisible}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          detailedData={detailedData}
          setDetailedData={setDetailedData}
          isDetailLoading={isDetailLoading}
          detailError={detailError}
        />

        <SparcWalkBegin
          isVisible={isFilterOverlayVisible}
          onClose={() => setFilterOverlayVisible(false)}
          selectedZone={beginZone}
          selectedCategory={beginCategory}
          setSelectedZone={setBeginZone}
          setSelectedCategory={setBeginCategory}
          zoneOptions={zoneOptions}
          categoryOptions={selectCategoryOptions}
          handleSaveFilters={() => {
            setFilterOverlayVisible(false);
            setShowInspectionScreen(true);
          }}
          onSave={() => {
            setFilterOverlayVisible(false);
            setShowInspectionScreen(true);
          }}
        />
      </ScrollView>

      {/* Centralized Error Modal */}
      <SparcWalkErrorModal />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  container: {
    paddingVertical: "1.5%",
    paddingHorizontal: Platform.OS === "web" ? "1.5%" : 16,
    backgroundColor: "#F5F9FF",
    minHeight: "100%",
  },
  errorText: {
    color: "#dc3545",
    padding: 10,
  },
  inspectionContainer: {
    flex: 1,
    paddingVertical: "1.5%",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    width: "100%",
  },
  successIcon: {
    marginRight: 8,
  },
  successMessageText: {
    fontSize: 16,
    color: "#155724",
    flex: 1,
  },
});

export default SparcWalkScreen;
